import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ScrollToTop } from "@/components/ScrollToTop";
import Dashboard from "@/pages/Dashboard";
import Landing from "@/pages/Landing";
import Help from "@/pages/Help";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import AdminConsole from "@/pages/admin/AdminConsole";
import Feedback from "@/pages/Feedback";
import RoomCapacity from "@/pages/planning/RoomCapacity";
import CateringEstimator from "@/pages/planning/CateringEstimator";
import StaffingPlanner from "@/pages/planning/StaffingPlanner";
import AVSpecs from "@/pages/planning/AVSpecs";
import WifiPredictor from "@/pages/planning/WifiPredictor";
import FloorPlan from "@/pages/planning/FloorPlan";
import { Navigate } from "react-router-dom";
import DancefloorCalculator from "@/pages/planning/DancefloorCalculator";
import SyncTimer from "@/pages/execution/SyncTimer";
import JoinTimer from "@/pages/execution/JoinTimer";
import DecibelMeter from "@/pages/execution/DecibelMeter";
import DinnerTally from "@/pages/execution/DinnerTally";
import ContractScanner from "@/pages/solver/ContractScanner";
import EmergencyScript from "@/pages/solver/EmergencyScript";
import UnitConverter from "@/pages/solver/UnitConverter";
import Settings from "@/pages/Settings";

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <div className="App min-h-screen bg-background bg-texture">
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              {/* Public Landing */}
              <Route path="/" element={<Landing />} />

              {/* Public Help Centre */}
              <Route path="/help" element={<Help />} />
              {/* Legacy help route → new hub with anchor */}
              <Route path="/planning/floor-plan/help" element={<Navigate to="/help#floor-plan" replace />} />

              {/* Auth Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              
              {/* Protected Routes */}
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              
              {/* Planning Suite */}
              <Route path="/planning/room-capacity" element={<ProtectedRoute><RoomCapacity /></ProtectedRoute>} />
              <Route path="/planning/catering" element={<ProtectedRoute><CateringEstimator /></ProtectedRoute>} />
              <Route path="/planning/staffing" element={<ProtectedRoute><StaffingPlanner /></ProtectedRoute>} />
              <Route path="/planning/av-specs" element={<ProtectedRoute><AVSpecs /></ProtectedRoute>} />
              <Route path="/planning/wifi" element={<ProtectedRoute><WifiPredictor /></ProtectedRoute>} />
              <Route path="/planning/floor-plan" element={<ProtectedRoute><FloorPlan /></ProtectedRoute>} />
              <Route path="/planning/dancefloor-calculator" element={<ProtectedRoute><DancefloorCalculator /></ProtectedRoute>} />
              
              {/* Execution Suite */}
              <Route path="/execution/timer" element={<ProtectedRoute><SyncTimer /></ProtectedRoute>} />
              <Route path="/execution/timer/:code" element={<ProtectedRoute><JoinTimer /></ProtectedRoute>} />
              <Route path="/execution/decibel" element={<ProtectedRoute><DecibelMeter /></ProtectedRoute>} />
              <Route path="/execution/tally" element={<ProtectedRoute><DinnerTally /></ProtectedRoute>} />
              {/* Tally view via shared code is intentionally PUBLIC — guests can collaborate without an account */}
              <Route path="/execution/tally/:code" element={<DinnerTally />} />
              
              {/* Problem Solver Suite */}
              <Route path="/solver/contract" element={<ProtectedRoute><ContractScanner /></ProtectedRoute>} />
              <Route path="/solver/emergency" element={<ProtectedRoute><EmergencyScript /></ProtectedRoute>} />
              <Route path="/solver/converter" element={<ProtectedRoute><UnitConverter /></ProtectedRoute>} />
              
              {/* Settings */}
              <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              
              {/* Feedback */}
              <Route path="/feedback" element={<ProtectedRoute><Feedback /></ProtectedRoute>} />
              
              {/* Admin */}
              <Route path="/admin/console" element={<ProtectedRoute adminOnly><AdminConsole /></ProtectedRoute>} />
            </Routes>
          </BrowserRouter>
          <Toaster 
            position="top-right" 
            toastOptions={{
              style: {
                background: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                color: 'hsl(var(--foreground))',
              },
            }}
          />
        </div>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
